<template>
  <span v-if="null === product" class="ui-loader" />
  <template v-else>
    <Breadcrumbs :breadcrumbs="breadcrumbs" />

    <div class="product">
      <Navigation ref="navigationRef" :tabs="tabs" :product="product" />
      <div
        class="product__card"
        :class="{ product__container: !isRouteMatched('product_summary') }"
      >
        <RouterView
          :product="product"
          :navigation-height="navigationHeight"
          :product-hash="dtoProduct.hash"
        />
        <PageFooter
          v-if="!isRouteMatched('product_summary')"
          :product="product"
        />
      </div>
      <BuyBar :product="product" />
    </div>
  </template>
</template>

<script setup>
import Breadcrumbs from "~/modules/shared/breadcrumbs/Breadcrumbs.vue";
import Navigation from "~/modules/product/ui/components/Navigation.vue";
import { useAPI, useAsyncAPI } from "~/uses/useMyFetch";
import { Product } from "~/models/product.model";
import PageFooter from "~/modules/product/ui/components/PageFooter.vue";
import { trackEvent } from "~/utils/trackEvent";
import BuyBar from "~/modules/product/ui/components/BuyBar.vue";
import { useCategoriesStore } from "~/store/categoriesStore";

const route = useRoute();
const config = useRuntimeConfig();

const product = ref(null);
const navigationRef = ref(null);
const { height: navigationHeight } = useElementSize(navigationRef, {
  height: 0,
});

const categoriesStore = useCategoriesStore();
const { getCategoryBySlug } = storeToRefs(categoriesStore);

const currentCategory = () => getCategoryBySlug.value(route.params.categorySlug);
const breadcrumbs = ref([]);

if (undefined === currentCategory()) {
  throw showError({
    statusCode: 404,
    statusMessage: "non-existent category slug",
  });
}

const { data: dtoProduct } = await useAsyncData(
  "fetchProduct",
  async () => {
    const { data, hash } = await useAsyncAPI("/catalog/product/item/slug", {
      key: "product",
      params: {
        slug: route.params.productSlug,
        "fields[]": [
          "contents",
          "attachments",
          "description",
          "specifications",
        ],
      },
      withHash: true,
    });

    if (data?.categories[0].id !== currentCategory().id) {
      throw showError({
        statusCode: 404,
        statusMessage: "category doesn't match to product",
      });
    }

    return {
      ...data,
      hash,
    };
  },
  {
    watch: [() => route.params.productSlug],
  },
);

const { data: extendedProductData } = await useAsyncData(
  "fetchExtendedProductData",
  async () => {
    const [reviews, configurations] = await Promise.all([
      fetchReviews(dtoProduct.value.code, 1, 3).catch(() => {
        return {};
      }),
      useAPI("/catalog/product/configurations", {
        params: {
          productId: dtoProduct.value.id,
        },
      }),
    ]);

    return { reviews, configurations };
  },
  { watch: [() => dtoProduct.value] },
);


const buildBreadcrumbs = (category) => {
  breadcrumbs.value.unshift({
    name: category.name,
    routeName: "category",
    params: {
      categorySlug: category.slug,
    },
  });

  if (null !== category.parent) {
    return buildBreadcrumbs(category.parent);
  }

  breadcrumbs.value.unshift({
    name: "@Catalog",
    routeName: "catalog",
  });

  breadcrumbs.value.push({
    name: product.value?.name,
    routeName: "product_summary",
    params: {
      categorySlug: product.value?.getMainCategory().slug,
      productSlug: product.value?.slug,
    },
  });
};

watch(
  dtoProduct,
  async () => {
    if (null === dtoProduct.value) {
      throw showError({
        statusCode: 404,
        statusMessage: "product doesn't exist",
      });
    }

    product.value = new Product(dtoProduct.value);
    breadcrumbs.value = [];
    buildBreadcrumbs(currentCategory());
  },
  {
    immediate: true,
    deep: true,
  },
);

const tabs = [
  {
    title: "@All about product",
    routeName: "product_summary",
  },
  {
    title: "@Specifications",
    routeName: "product_specifications",
    isShow: () => 0 !== product.value?.specifications.length,
  },
  {
    title: "@Reviews",
    routeName: "product_reviews",
  },
  {
    title: "@Video",
    routeName: "product_videos",
    isShow: () => 0 !== product.value?.getVideos().length,
  },
  {
    title: "@Downloads",
    routeName: "product_downloads",
    isShow: () => 0 !== product.value?.getDownloads().length,
  },
];

const getJsonLd = (product, reviews) => {
  const getAggregateRating = (product) => {
    if (0 === product.getReviewsAverageRating()) {
      return undefined;
    }

    return {
      "@type": "AggregateRating",
      ratingValue: product.getReviewsAverageRating(),
      reviewCount: product.reviews.count,
    };
  };

  const getAvailabilityStarts = (product) => {
    if (product.isOrderable) {
      return undefined;
    }

    const date = new Date();
    const availabilityDate = date.setDate(date.getDate() + 14);

    return new Date(availabilityDate).toISOString();
  };

  const getShippingDetails = (product) => {
    return getProductDeliveryOptions(product).map((delivery) => {
      return {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          minValue: delivery.isFreeDelivery ? 0 : 70,
          currency: "UAH",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 3,
            unitCode: "DAY",
          },
          handlingTime: {
            minValue: 1,
            maxValue: 3,
            unitCode: "DAY",
          },
        },
      };
    });
  };

  const getReviews = (reviews) => {
    if (0 === reviews.length) {
      return undefined;
    }

    return reviews.map((review) => {
      return {
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.name,
        },
        datePublished: review.createdAt.replace(/T.*$/, ""),
        reviewBody: review.content,
        reviewRating: {
          "@type": "Rating",
          bestRating: 5,
          worstRating: 1,
          ratingValue: review.rating,
        },
      };
    });
  };

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    aggregateRating: getAggregateRating(product),
    name: product.getName(),
    sku: product.code,
    image: product.getMainImage("large"),
    description: product.getDescription().replace(/<[^>]*>/g, ""),
    offers: {
      "@type": "Offer",
      availability: product.isOrderable
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      availabilityStarts: getAvailabilityStarts(product),
      price: product.getPrice(Product.PRICE_TYPE.CURRENT),
      priceCurrency: product.getCurrency(Product.PRICE_TYPE.CURRENT),
      shippingDetails: getShippingDetails(product),
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "UA",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 14,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
    },
    review: getReviews(reviews),
  };
};

watch(
  product,
  () => {
    if (null !== product.value) {
      const currentTab = tabs.find((tab) => isRouteMatched(tab.routeName));

      if (undefined !== currentTab.isShow && !currentTab.isShow()) {
        return navigateTo({ name: getLocalizedRouteName("product_summary") });
      }

      if (import.meta.client) {
        //Add product to user seen store
        useAPI("/catalog/product/seen/add", {
          method: "POST",
          body: {
            productId: product.value?.id,
          },
        });

        const event = {
          event: "view_item",
          ecommerce: {
            items: [
              {
                item_name: product.value?.slug,
                item_id: product.value?.code,
                price: product.value?.getPrice(Product.PRICE_TYPE.CURRENT),
                item_brand: product.value?.manufacturer.name,
                item_category: route.params.categorySlug,
                quantity: 1,
              },
            ],
          },
        };
        //Add view product to google-analytics
        trackEvent(event);
      }
    }
  },
  { deep: true },
);

useSeoMeta({
  title: product.value.getName(),
  description: product.value.getDescription().replace(/<[^>]*>/g, ""),
  ogUrl: config.public.appUrl + route.path,
  ogImage: product.value.getMainImage("large"),
});

if (
  "outOfProduction" !== product.value?.status &&
  undefined !== product.value?.getPrice(Product.PRICE_TYPE.CURRENT) &&
  import.meta.server
) {
  useHead({
    script: [
      {
        type: "application/ld+json",
        children: getJsonLd(
          product.value,
          extendedProductData.value.reviews.data ?? [],
        ),
      },
    ],
  });
}
</script>

<style scoped lang="scss">
.product {
  width: 100%;

  @include flex-container(column, flex-start, center);

  padding: 0 16px 40px;

  @include mobile {
    padding-inline: 0;
  }

  &__card {
    @extend %width-main;
  }
}
</style>
